import { Box, Text, Title, Paper, Group, Stack, Badge, Grid, Image, Flex } from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import { CoverTestData } from "../hooks/useLazyEyeData";
import { AgeBand, COVER_THRESHOLDS_DEG, isCriticalPeriod } from "../utils/lazyEyeRiskModel";
import { RISK_LEVEL_COLOR } from "@constants/resultColors";
import { RiskLevel } from "../utils/severity";

interface LazyEyeEyeAlignmentCardProps {
  coverTestData: CoverTestData;
  riskLevel: RiskLevel;
  ageBand: AgeBand;
}

function formatMagnitude(value: number, isDegrees: boolean): string {
  if (!Number.isFinite(value)) return "—";
  return isDegrees ? `${value.toFixed(2)}°` : value.toFixed(3);
}

function badgeDescriptor(level: RiskLevel): string {
  if (level === "low") return "Aligned";
  if (level === "moderate") return "Slight deviation detected";
  return "Notable deviation detected";
}

function explanationText(level: RiskLevel, ageBand: AgeBand): string {
  if (level === "low") return "No manifest deviation. Continue routine check-ups.";
  if (level === "moderate") {
    return isCriticalPeriod(ageBand)
      ? "Small deviation during the critical period — schedule a comprehensive exam."
      : "Small deviation, below referral threshold. Re-check at next routine exam.";
  }
  return `Within the strabismus referral range (≥ ${COVER_THRESHOLDS_DEG.highMean}° / ≈7 PD). Comprehensive eye-care evaluation recommended.`;
}

export function LazyEyeEyeAlignmentCard({
  coverTestData,
  riskLevel,
  ageBand,
}: LazyEyeEyeAlignmentCardProps) {
  const inDeg = coverTestData.isDataModeInDegrees;
  const color = RISK_LEVEL_COLOR[riskLevel];

  return (
    <Paper shadow="sm" radius="md" p="sm" withBorder>
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <Group gap="md" align="flex-start" style={{ flex: 1, minWidth: "300px" }}>
          <Box
            style={{
              width: 48,
              height: 48,
              backgroundColor: "var(--mantine-color-cyan-6)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconEye size={24} color="white" />
          </Box>
          <Stack gap={4} style={{ flex: 1 }}>
            <Title order={4} mb={0}>
              Eye Alignment (Cover Test)
            </Title>
            <Text size="sm" c="dimmed">
              Detects manifest deviation when one eye is briefly covered
            </Text>
          </Stack>
        </Group>

        <Group align="center" gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Badge color={color} size="lg" radius="md">
            {riskLevel.toUpperCase()}
          </Badge>
          <Text size="sm" style={{ whiteSpace: "nowrap" }}>
            {badgeDescriptor(riskLevel)}
          </Text>
        </Group>
      </Group>

      <Flex gap="md" direction="row" wrap="nowrap" mt="sm">
        <Box style={{ flex: 1 }}>
          <Grid gutter="sm" mt="sm" className="print-row-4-4-4">
            <Grid.Col span={4}>
              <Stack gap={6}>
                <Text fw={600}>Overall</Text>
                <Text size="sm" c="dimmed">
                  Mean Offset
                </Text>
                <Text fw={500}>{formatMagnitude(coverTestData.overallMetrics.mean, inDeg)}</Text>
                <Text size="sm" c="dimmed">
                  Fixation Stability
                </Text>
                <Text fw={500}>
                  {formatMagnitude(coverTestData.overallMetrics.stability, inDeg)} (
                  {coverTestData.overallFixationDescriptor})
                </Text>
              </Stack>
            </Grid.Col>

            <Grid.Col span={4}>
              <Stack gap={6}>
                <Text fw={600}>Left Eye</Text>
                <Text size="sm" c="dimmed">
                  Mean Offset
                </Text>
                <Text fw={500}>{formatMagnitude(coverTestData.leftEyeMetrics.mean, inDeg)}</Text>
                <Text size="sm" c="dimmed">
                  Fixation Stability
                </Text>
                <Text fw={500}>
                  {formatMagnitude(coverTestData.leftEyeMetrics.stability, inDeg)} (
                  {coverTestData.leftFixationDescriptor})
                </Text>
              </Stack>
            </Grid.Col>

            <Grid.Col span={4}>
              <Stack gap={6}>
                <Text fw={600}>Right Eye</Text>
                <Text size="sm" c="dimmed">
                  Mean Offset
                </Text>
                <Text fw={500}>{formatMagnitude(coverTestData.rightEyeMetrics.mean, inDeg)}</Text>
                <Text size="sm" c="dimmed">
                  Fixation Stability
                </Text>
                <Text fw={500}>
                  {formatMagnitude(coverTestData.rightEyeMetrics.stability, inDeg)} (
                  {coverTestData.rightFixationDescriptor})
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>

          <Paper mt="sm" p="sm" withBorder radius="md">
            <Text size="sm" c="dimmed">
              <Text span fw={600}>
                What this means:
              </Text>{" "}
              {explanationText(riskLevel, ageBand)}
            </Text>
          </Paper>
        </Box>

        <Box style={{ width: 200, flexShrink: 0 }}>
          <Image
            src="/img/visuals/cover-test.png"
            alt="Cover Test Illustration"
            radius="md"
            fit="contain"
            h={200}
          />
        </Box>
      </Flex>
    </Paper>
  );
}
